import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Heart, MapPin } from "lucide-react";
import CloudWave from "@/components/CloudWave";

const Footer = () => {
  const containerVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.8, ease: "easeOut", staggerChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } }
  };

  return (
    <>
      <CloudWave position="bottom" color="#0e7490" />
      <motion.footer
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
        className="relative bg-cyan-700 overflow-hidden"
      >
        {/* Subtle decorations */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-6 left-10 w-16 h-16 bg-white/5 rounded-full" />
          <div className="absolute bottom-10 right-16 w-20 h-20 bg-white/5 rounded-full" />
          <div className="absolute top-1/2 left-1/3 w-12 h-12 bg-cyan-400/10 rounded-full" />
        </div>

        <div className="relative z-10 container mx-auto px-6 py-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-8">
            <motion.div variants={itemVariants} className="lg:col-span-2">
              <div className="flex items-center gap-2 mb-4">
                <motion.h3 
                  className="text-2xl font-fredoka font-bold text-white"
                  whileHover={{ scale: 1.05 }}
                  transition={{ type: "spring", stiffness: 400 }}
                >
                  🌈 TinyMinds
                </motion.h3>
              </div>
              <p className="text-cyan-100 mb-4 leading-relaxed max-w-md font-medium">
                Making learning fun and engaging for young minds. Explore educational content 
                designed to inspire curiosity and creativity in children.
              </p>
              <div className="flex items-center gap-4 text-sm text-cyan-200">
                <motion.div className="flex items-center gap-2" whileHover={{ scale: 1.05 }}>
                  <MapPin className="w-4 h-4" />
                  <span className="font-medium">Made in India 🇮🇳</span>
                </motion.div>
              </div>
            </motion.div>

            <motion.div variants={itemVariants}>
              <p className="text-lg font-baloo font-semibold mb-4 text-orange-300">
                <strong>Quick Links ⚡</strong>
              </p>
              <div className="space-y-2">
                {[
                  { name: "Home", path: "/" },
                  { name: "Alphabet & Numbers", path: "/alphabet" },
                  { name: "Math", path: "/math" },
                  { name: "Science", path: "/science" },
                  { name: "English Learning", path: "/english-learning" },
                  { name: "Geography", path: "/geography" },
                  { name: "Stories", path: "/stories" },
                  { name: "Games", path: "/games" },
                  { name: "Brain Teasers", path: "/brain-teasers" },
                  { name: "World Explorer", path: "/world-explorer" },
                  { name: "Printables", path: "/printables-zone" },
                  { name: "Videos", path: "/yt-gallery" },
                  { name: "Blog", path: "/blog" },
                ].map((link, index) => (
                  <motion.div
                    key={link.name}
                    initial={{ opacity: 0, x: -10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    whileHover={{ x: 5, scale: 1.05 }}
                  >
                    <Link 
                      to={link.path} 
                      className="block text-cyan-100 hover:text-orange-300 transition-colors duration-200 font-medium"
                    >
                      {link.name}
                    </Link>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <motion.div variants={itemVariants}>
              <p className="text-lg font-baloo font-semibold mb-4 text-orange-300">
                <strong>Company 🏢</strong>
              </p>
              <div className="space-y-2">
                {[
                  { name: "About", path: "/about" },
                  { name: "Contact", path: "/contact" },
                  { name: "FAQ", path: "/contact#faq" },
                  { name: "Privacy Policy", path: "/privacy-policy" },
                  { name: "Terms Of Service", path: "/terms-of-service" },
                ].map((link, index) => (
                  <motion.div
                    key={link.name}
                    initial={{ opacity: 0, x: -10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    whileHover={{ x: 5, scale: 1.05 }}
                  >
                    <Link 
                      to={link.path} 
                      className="block text-cyan-100 hover:text-orange-300 transition-colors duration-200 font-medium"
                    >
                      {link.name}
                    </Link>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>

          <motion.div variants={itemVariants} className="pt-8 border-t border-cyan-600">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <motion.div className="text-center md:text-left text-cyan-200" whileHover={{ scale: 1.02 }}>
                <p className="text-sm font-medium">
                  © {new Date().getFullYear()} TinyMinds. All rights reserved.
                </p>
              </motion.div>
              
              <motion.div 
                className="flex items-center gap-2 text-sm text-cyan-200"
                animate={{ opacity: [0.7, 1, 0.7] }}
                transition={{ duration: 3, repeat: Infinity }}
              >
                <span className="font-medium">Built with</span>
                <motion.div
                  animate={{ scale: [1, 1.3, 1] }}
                  transition={{ duration: 1.5, repeat: Infinity }}
                >
                  <Heart className="w-4 h-4 text-red-300" fill="currentColor" />
                </motion.div>
                <span className="font-medium">for children 💜</span>
              </motion.div>

              <motion.div className="text-sm text-cyan-300 text-center">
                <span className="font-medium">Only for Indian users. Prices include taxes. Digital downloads only.</span>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </motion.footer>
    </>
  );
};

export default Footer;
